// OWNER: FRONTEND-ADMIN. SPEC §15.7 Numbers (admin): pool table with ramp, caps, health vs baseline vs peers,
// SIP 603/608, registrations; status actions with a required note; ledger; buy flow with an explicit price confirmation.
import type { ComponentChildren } from 'preact';
import { useState } from 'preact/hooks';
import type { NumberStatus, OurNumber } from '../../../src/shared/api';
import { Empty, ErrorBanner, pct } from '../components/ui';
import { adminApi } from '../admin/endpoints';
import { BuyNumberModal, EditNumberModal, HandsetChecklist, ImportNumberModal, LedgerModal, StatusChangeModal } from '../admin/NumberParts';
import { SELF_TEST_HINT, alertActions, attestationLine, numberMenu, registrationLines, type StatusAction } from '../admin/numbers';
import { NUMBER_COLUMNS, type NumberColumnKey } from '../admin/layout';
import { formatPhone } from '../admin/phone';
import { OPS_TZ, rampDay, ymdInTz } from '../admin/dates';
import { FixedHead, RowMenu, StatusChip, inputValue, useLoad } from '../admin/ui';

type Dialog =
  | { kind: 'buy' } | { kind: 'import' }
  | { kind: 'edit'; n: OurNumber } | { kind: 'ledger'; n: OurNumber } | { kind: 'status'; n: OurNumber; action: StatusAction };

const STATUS_ORDER: NumberStatus[] = ['flagged', 'active', 'ramping', 'resting', 'retired'];

export function Numbers(_props: { params: Record<string, string> }) {
  const numbers = useLoad(() => adminApi.numbers(), []);
  const settings = useLoad(() => adminApi.settings(), []);
  const [dialog, setDialog] = useState<Dialog | null>(null);
  const [showRetired, setShowRetired] = useState(false);
  const [country, setCountry] = useState<'' | 'US' | 'CA'>('');
  const now = new Date();
  const today = ymdInTz(now, OPS_TZ);

  const replace = (n: OurNumber) => {
    const list = numbers.data ?? [];
    numbers.setData(list.some((x) => x.id === n.id) ? list.map((x) => (x.id === n.id ? n : x)) : [...list, n]);
  };
  const all = numbers.data ?? [];
  const rows = all
    .filter((n) => (showRetired || n.status !== 'retired') && (!country || n.country === country))
    .sort((a, b) => STATUS_ORDER.indexOf(a.status) - STATUS_ORDER.indexOf(b.status) || a.country.localeCompare(b.country) || a.e164.localeCompare(b.e164));
  const alerts = all.filter((n) => n.status !== 'retired' && n.health?.alert);
  const placeable = all.filter((n) => n.purpose === 'outbound' && (n.status === 'ramping' || n.status === 'active'));
  const capacity = (c: 'US' | 'CA') => placeable.filter((n) => n.country === c).reduce((a, n) => a + n.dailyCap, 0);
  const used = (c: 'US' | 'CA') => placeable.filter((n) => n.country === c).reduce((a, n) => a + n.todayDials, 0);
  const mainId = settings.data?.mainNumberId ?? null;
  const mainPretty = settings.data?.mainNumberE164 ? formatPhone(settings.data.mainNumberE164) : null;

  return (
    <div class="adm-page">
      <div class="adm-head">
        <div>
          <h1>Numbers</h1>
          <div class="adm-sub">
            Today's outbound capacity: US {used('US')}/{capacity('US')} · Canada {used('CA')}/{capacity('CA')} dials.
            Numbers are sticky per lead and never rotated; ramp 20 → 40 (day 3) → 60 (day 7), hard max 80.
          </div>
        </div>
        <div class="row">
          <button class="btn" onClick={() => setDialog({ kind: 'import' })}>Import existing…</button>
          <button class="btn btn-primary" onClick={() => setDialog({ kind: 'buy' })}>Buy a number…</button>
        </div>
      </div>

      {alerts.length ? (
        <div class="card stack" style="border-color:var(--red-ink)">
          <b>{alerts.length} number{alerts.length === 1 ? ' needs' : 's need'} a check</b>
          {alerts.map((n) => (
            <div class="row" style="flex-wrap:wrap">
              <span class="mono">{formatPhone(n.e164)}</span><StatusChip status={n.status} />
              <span style="flex:1;min-width:240px">{n.health?.alert}</span>
              <button class="btn btn-sm" onClick={() => setDialog({ kind: 'ledger', n })}>Ledger / test</button>
              {alertActions(n).map((a) => (
                <button class={`btn btn-sm${a.danger ? ' btn-danger' : ''}`} title={a.help} onClick={() => setDialog({ kind: 'status', n, action: a })}>{a.label}…</button>
              ))}
            </div>
          ))}
        </div>
      ) : null}

      <div class="adm-toolbar">
        <select value={country} onChange={(e) => setCountry(inputValue(e) as '' | 'US' | 'CA')}>
          <option value="">US + Canada</option><option value="US">US</option><option value="CA">Canada</option>
        </select>
        <label class="adm-check"><input type="checkbox" checked={showRetired} onChange={(e) => setShowRetired((e.target as HTMLInputElement).checked)} /> Show retired</label>
        <span class="grow" />
        {numbers.loading ? <span class="adm-sub">Loading…</span> : null}
        <button class="btn btn-sm" onClick={() => void numbers.reload()}>Refresh</button>
      </div>
      <ErrorBanner error={numbers.error} />
      <ErrorBanner error={settings.error} />
      {numbers.data && rows.length === 0 ? <Empty>{all.length === 0 ? 'No numbers yet. Buy one per metro you call (plus a main callback number).' : 'No numbers match.'}</Empty> : null}

      {rows.length ? (
        <div class="adm-table-wrap">
          <table class="adm-fixed">
            <FixedHead cols={NUMBER_COLUMNS} />
            <tbody>
              {rows.map((n) => {
                const h = n.health;
                const day = rampDay(n.rampStartedAt, now);
                const sip = (h?.sip603 ?? 0) + (h?.sip608 ?? 0);
                const cells: Record<NumberColumnKey, ComponentChildren> = {
                  number: (
                    <>
                      <span class="mono" style="white-space:nowrap">{formatPhone(n.e164)}</span>
                      <span class="adm-cell-sub" style="white-space:normal">{n.country}{n.region ? `-${n.region}` : ''} · {n.purpose === 'main' ? 'main' : 'outbound'}{n.id === mainId ? ' · callback number' : ''}</span>
                    </>
                  ),
                  metro: n.metro ?? '—',
                  status: <><StatusChip status={n.status} />{h?.alert ? <div><span class="chip chip-bad" title={h.alert}>alert</span></div> : null}</>,
                  today: n.purpose === 'main'
                    ? <span class="muted">no dials</span>
                    : <><span class={n.todayDials >= n.dailyCap ? 'chip chip-warn' : ''}>{n.todayDials}/{n.dailyCap}</span><span class="adm-cell-sub">{day === null ? '' : `day ${day}`}</span></>,
                  connect: <>{pct(h?.connectRate)}<span class="adm-cell-sub">{h ? `${h.dials} dials` : ''}</span></>,
                  baseline: pct(h?.baselineConnectRate),
                  peers: pct(h?.peerConnectRate),
                  short: pct(h?.shortCallRate, 0),
                  sip: h ? <span class={sip ? 'chip chip-bad' : ''}>{h.sip603}/{h.sip608}</span> : '—',
                  registered: (
                    <div class="adm-reg">
                      {registrationLines(n, today).map((l) => <span class={l.state === 'n/a' ? 'na' : l.state} title={l.title}>{l.text}</span>)}
                      {((a) => <span class={a.state === 'n/a' ? 'na' : a.state} title={a.title}>{a.text}</span>)(attestationLine(n, today, mainPretty))}
                    </div>
                  ),
                  actions: (
                    <RowMenu label="Manage" ariaLabel={`Manage ${formatPhone(n.e164)}`} items={numberMenu(n).map((it, i, all) => ({
                      label: it.label,
                      danger: it.kind === 'status' && !!it.danger,
                      separatorBefore: it.kind === 'status' && all[i - 1]?.kind !== 'status',
                      title: it.kind === 'status' ? it.action.help : undefined,
                      onSelect: () => setDialog(it.kind === 'status' ? { kind: 'status', n, action: it.action } : it.kind === 'ledger' ? { kind: 'ledger', n } : { kind: 'edit', n }),
                    }))} />
                  ),
                };
                return (
                  <tr class={n.status === 'retired' ? 'adm-dim' : ''}>
                    {NUMBER_COLUMNS.map((c) => <td class={c.num ? 'num' : undefined}>{cells[c.key]}</td>)}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}
      <div class="adm-sub">
        Health window: 3 days, needs ≥ 40 dials. An alert fires when the connect rate falls under half of the number's own 14-day baseline or half of the peer median, or on any SIP 603/608.
        “—” = not enough dials to measure.
      </div>
      <div class="adm-sub">
        <b>STIR</b> is the STIR/SHAKEN attestation carriers gave the last self-test call: green = A (full), amber = B or C, red = none or failed.
        {' '}{SELF_TEST_HINT}{mainPretty ? ` (${mainPretty})` : ''}; the dialer reads the result from that inbound call. Re-check every active number monthly.
        {!mainPretty && settings.data ? ' No main number is set yet: pick one in Settings → Calling first.' : ''}
      </div>

      <HandsetChecklist />

      {dialog?.kind === 'buy' ? <BuyNumberModal onBought={replace} onClose={() => setDialog(null)} /> : null}
      {dialog?.kind === 'import' ? <ImportNumberModal onImported={replace} onClose={() => setDialog(null)} /> : null}
      {dialog?.kind === 'edit' ? <EditNumberModal number={dialog.n} onDone={replace} onClose={() => setDialog(null)} /> : null}
      {dialog?.kind === 'ledger' ? (
        <LedgerModal number={dialog.n} mainNumber={mainPretty} onClose={() => setDialog(null)} onStatus={(action) => setDialog({ kind: 'status', n: dialog.n, action })} />
      ) : null}
      {dialog?.kind === 'status' ? <StatusChangeModal number={dialog.n} action={dialog.action} onDone={replace} onClose={() => setDialog(null)} /> : null}
    </div>
  );
}
